/**
 * Tiny localStorage wrapper.
 *
 * - All access goes through readJson / writeJson so we have one place that
 *   handles parse failures, missing keys, quota, and private-browsing throws.
 * - On any error, returns the provided default and logs once. The game keeps
 *   working — saves just don't persist.
 */

const STORAGE_KEY = 'splat-chimp.v1';

let warnedRead = false;
let warnedWrite = false;

export function readJson<T>(fallback: T): T {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw);
    // Sanity: must be an object; otherwise treat as missing.
    if (parsed && typeof parsed === 'object') return parsed as T;
    return fallback;
  } catch (err) {
    if (!warnedRead) {
      console.warn('splat-chimp: could not read save data, using defaults', err);
      warnedRead = true;
    }
    return fallback;
  }
}

export function writeJson(value: unknown): boolean {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    return true;
  } catch (err) {
    if (!warnedWrite) {
      console.warn('splat-chimp: could not save data (private mode? quota?)', err);
      warnedWrite = true;
    }
    return false;
  }
}

/** For tests / debug: wipe everything. */
export function clearSave(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
